import EmblaCarousel from 'embla-carousel';
import {
    ANNUAL,
    EVERY_30_DAYS,
    getFilteredSortedPlans,
    getIntervalShortLabel,
    getPlanButtonText,
    formatFeature,
    isPlanButtonDisabled,
    translateMe,
    deepEqual,
    emitEvent,
    preparePlanFeatures,
    getSortedPlanDetails,
    getPrimaryPlanDetail,
    getAdditionalPlanDetails,
    getPlanChangeConfirmationContent,
    isPlanDowngrade,
    isFreePlan,
} from '@/helpers/helper.js';

/**
 * PlanCardSliderPolaris custom element
 * @property {Array} plans - List of plan objects to display
 * @property {Array} features - List of feature objects
 * @property {Object} currentPlan - The current plan object
 * @property {Boolean} hasActiveCharge - Whether the user has an active charge
 * @property {String} shopifyPlan - The current Shopify plan
 * @property {Object} promotionalDiscount - Promotional discount object
 * @property {String} selectedInterval - Selected interval (monthly/annual)
 */
class PlanCardSliderPolaris extends HTMLElement {
    static get observedAttributes() {
        return [
            'has-active-charge',
            'shopify-plan',
            'selected-interval',
            'large-card',
        ];
    }

    constructor() {
        super();
        this.attachShadow({mode: 'open'});

        // external libraries
        this._embla = null;

        // data
        this._plans = [];
        this._features = [];
        this._currentPlan = {};
        this._hasActiveCharge = false;
        this._shopifyPlan = '';
        this._selectedInterval = EVERY_30_DAYS;
        this._promotionalDiscount = null;
        this._showOnlyHighlights = false;
        this._largeCard = false;

        this.anyMonthlyPlanHasDiscount = false;
        this.anyAnnualPlanHasDiscount = false;

        // UI state
        this._initialized = false;
        this._loadingPlanId = null;
        this._selectedPlanId = null;
        this._pendingSnapIndex = null;

        // observers
        this._resizeObserver = null;
        this._featureResizeObserver = null;
        this._headerResizeObserver = null;
        this._dotListeners = [];
    }

    connectedCallback() {
        if (!this._initialized) {
            this._initialized = true;
            this._renderShell();
        }
        if (!this._resizeObserver) {
            this._resizeObserver = new ResizeObserver(() => {
                this._embla?.reInit();
                this._setDynamicSlideWidths();
            });
            this._resizeObserver.observe(this);
        }
        this._render();
    }

    disconnectedCallback() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case 'has-active-charge':
                this.hasActiveCharge = newValue !== null && newValue !== 'false';
                break;
            case 'shopify-plan':
                this.shopifyPlan = newValue;
                break;
            case 'selected-interval':
                this.selectedInterval = newValue;
                break;
            case 'large-card':
                this._largeCard = newValue !== null && newValue !== 'false';
                break;
        }
        this._render();
    }


    // --- Property Getters/Setters ---
    get plans() { return this._plans; }
    set plans(value) {
        if (!deepEqual(this._plans, value)) {
            this._plans = Array.isArray(value) ? value : [];
            this._selectedPlanId = null;
            this._render();
        }
    }

    get features() { return this._features; }
    set features(value) {
        if (!deepEqual(this._features, value)) {
            this._features = Array.isArray(value) ? value : [];
            this._render();
        }
    }

    get currentPlan() { return this._currentPlan; }
    set currentPlan(value) {
        if (!deepEqual(this._currentPlan, value)) {
            this._currentPlan = value || {};
            this._render();
        }
    }

    get hasActiveCharge() { return this._hasActiveCharge; }
    set hasActiveCharge(value) {
        if (this._hasActiveCharge !== !!value) {
            this._hasActiveCharge = !!value;
            this._render();
        }
    }

    get shopifyPlan() { return this._shopifyPlan; }
    set shopifyPlan(value) {
        if (this._shopifyPlan !== value) {
            this._shopifyPlan = value;
            this._render();
        }
    }

    get promotionalDiscount() { return this._promotionalDiscount; }
    set promotionalDiscount(value) {
        if (!deepEqual(this._promotionalDiscount, value)) {
            this._promotionalDiscount = value;
            this._render();
        }
    }

    get selectedInterval() { return this._selectedInterval; }
    set selectedInterval(value) {
        if (this._selectedInterval !== value) {
            this._selectedInterval = value;
            this._selectedPlanId = null;
            this._render();
        }
    }

    get showOnlyHighlights() { return this._showOnlyHighlights; }
    set showOnlyHighlights(value) {
        const parsed = !!value;
        if (this._showOnlyHighlights !== parsed) {
            this._showOnlyHighlights = parsed;
            this._render();
        }
    }

    get largeCard() { return this._largeCard; }
    set largeCard(value) {
        const parsed = !!value;
        if (this._largeCard !== parsed) {
            this._largeCard = parsed;
            this._render();
        }
    }


    // ---- Internal Helpers ----
    _getDiscountFlag() {
        return this._selectedInterval === ANNUAL ? this.anyAnnualPlanHasDiscount : this.anyMonthlyPlanHasDiscount;
    }

    _getPlansByInterval(interval) {
        try {
            let filterData = getFilteredSortedPlans(
                Array.isArray(this._plans) ? this._plans : [],
                interval,
                this.promotionalDiscount,
                this.shopifyPlan,
                this.currentPlan,
                this.hasActiveCharge
            );
            if (this._selectedInterval === ANNUAL) {
                this.anyAnnualPlanHasDiscount = filterData.anyPlanHasDiscount;
            } else {
                this.anyMonthlyPlanHasDiscount = filterData.anyPlanHasDiscount;
            }
            return Array.isArray(filterData.filteredPlans) ? filterData.filteredPlans : [];
        } catch (e) {
            console.error('Error in _getPlansByInterval:', e, this._plans);
            return [];
        }
    }

    _getSlidesPerView() {
        const width = window.innerWidth;
        let slidesPerView;
        if (width <= 640) slidesPerView = 1;
        else if (width <= 1024) slidesPerView = 2;
        else if (width < 1440) slidesPerView = 3;
        else if (width < 1600) slidesPerView = 4;
        else slidesPerView = 5;
        if (this._largeCard && slidesPerView >= 4) {
            slidesPerView -= 1;
        }
        return Math.min(slidesPerView, this._plans.length || 1);
    }

    // --- Render Methods ---
    _renderShell() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                }
                .app-manager__width-container,
                .app-manager__width-container *,
                .app-manager__width-container *::before,
                .app-manager__width-container *::after {
                    box-sizing: border-box;
                }
                .plan-cards-slider {
                    position: relative;
                    padding-top: 8px;
                    padding-bottom: 8px;
                    margin-left: auto;
                    margin-right: auto;
                }
                .app-manager__width-container.plan-cards-slider {
                    padding-left: 96px;
                    padding-right: 96px;
                    max-width: calc(1200px + 8px);
                }
                .plan-cards-slider__embla {
                    overflow: hidden;
                    width: 100%;
                }
                .plan-cards-slider__container {
                    display: flex;
                    gap: 0;
                    align-items: stretch;
                }
                .plan-cards-slider__slide {
                    flex: 0 0 25%;
                    min-width: 0;
                }
                .plan-card {
                    display: flex;
                    flex-direction: column;
                    width: calc(100% - 8px);
                    height: 100%;
                    margin: auto;
                    padding: 16px;
                    position: relative;
                    z-index: 0;
                    isolation: isolate;
                    background-color: #fff;
                    border: 1px solid #e5e5e5;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px 0 rgba(0,0,0,0.04);
                    box-sizing: border-box;
                    transition: box-shadow 0.2s ease, border-color 0.2s ease;
                }
                .plan-card:hover {
                    border-color: #b0d8f0;
                    box-shadow: 0 0 5px 0 #b0d8f0 inset !important;
                }
                .plan-card--highlighted,
                .plan-card--highlighted:hover,
                .plan-card--selecting,
                .plan-card--selecting:hover {
                    border: 2px solid #39A3D9 !important;
                    box-shadow: 0 0 21px 0 #39A3D9 inset !important;
                }
                .plan-card__header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 8px;
                    margin-bottom: 16px;
                    pointer-events: none;
                }
                .plan-card__heading {
                    color: #1a1a1a;
                    font-size: 20px;
                    line-height: 24px;
                    font-weight: 700;
                    margin: 0;
                    text-align: left;
                    flex: 1;
                }
                .plan-card__badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 1px 8px;
                    width: max-content;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 18px;
                    background-color: #d5ebff;
                    color: #003a51;
                }
                .plan-card__badge.plan-card__badge-trial {
                    margin-top: 16px;
                    pointer-events: none;
                }
                .plan-card__trial-placeholder {
                    height: 28px;
                    margin-top: 10px;
                    margin-bottom: 2px;
                }
                .plan-card__price-section {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    pointer-events: none;
                }
                .plan-card__price-section--any-plan-has-discount {
                    min-height: 56px;
                }
                .plan-card__strike-price-row {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: baseline;
                    min-height: 22px;
                    gap: 2px;
                    pointer-events: none;
                }
                .plan-card__strike-price {
                    font-size: 14px;
                    line-height: 20px;
                    font-weight: 400;
                    margin: 0;
                    text-decoration: line-through;
                    color: #6d7175;
                    pointer-events: none;
                }
                .plan-card__price-row {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: baseline;
                    gap: 2px;
                    pointer-events: none;
                }
                .plan-card__price {
                    font-size: 24px;
                    line-height: 32px;
                    font-weight: 700;
                    margin: 0;
                    color: #1a1a1a;
                    pointer-events: none;
                }
                .plan-card__plan-interval {
                    font-size: 13px;
                    line-height: 20px;
                    color: #6d7175;
                    pointer-events: none;
                }
                .plan-card__strike-price-row .plan-card__plan-interval {
                    font-size: 12px;
                    color: #6d7175;
                }
                .plan-card__badge-group {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .plan-card__badge-discount {
                    white-space: nowrap;
                }
                .plan-card__badge-discount--active {
                    background-color: #cdfee1;
                    color: #0c5132;
                }
                .plan-card__badge-info {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    color: #6d7175;
                    line-height: 0;
                    cursor: help;
                    pointer-events: auto;
                }
                .plan-card__badge-tooltip {
                    display: none;
                    position: absolute;
                    top: calc(100% + 6px);
                    right: 0;
                    z-index: 5;
                    width: max-content;
                    max-width: 240px;
                    padding: 8px 10px;
                    border-radius: 8px;
                    background-color: #1a1a1a;
                    color: #ffffff;
                    font-size: 12px;
                    line-height: 16px;
                    font-weight: 400;
                    text-align: left;
                    white-space: normal;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }
                .plan-card__badge-tooltip span {
                    display: block;
                }
                .plan-card__badge-info:hover .plan-card__badge-tooltip,
                .plan-card__badge-info:focus .plan-card__badge-tooltip,
                .plan-card__badge-info:focus-within .plan-card__badge-tooltip {
                    display: block;
                }
                .plan-card__description-text {
                    font-size: 13px;
                    line-height: 20px;
                    color: #303030;
                    margin: 0;
                    text-align: left;
                    pointer-events: none;
                }
                .plan-card__description-text span {
                    font-weight: 650;
                }
                .plan-card__button-wrapper {
                    margin-bottom: 0;
                    margin-top: 8px;
                    position: relative;
                    z-index: 3;
                    pointer-events: auto;
                }
                .plan-card__button-wrapper s-button,
                .plan-card__button-wrapper > * {
                    pointer-events: auto;
                }
                .plan-card__feature-list {
                    list-style: none;
                    padding: 0;
                    margin: 16px 0;
                    pointer-events: none;
                }
                .plan-card__feature {
                    margin: 0 0 6px 0;
                    font-size: 13px;
                    line-height: 20px;
                    display: flex;
                    justify-content: flex-start;
                    align-items: flex-start;
                    gap: 6px;
                    font-weight: 400;
                    color: #303030;
                }
                .plan-card__feature-icon {
                    height: 20px;
                    flex-shrink: 0;
                    margin-top: 0;
                }
                .plan-card__detail-tooltip-wrapper {
                    margin-top: auto;
                    pointer-events: none;
                }
                .plan-card__detail-tooltip {
                    padding-top: 12px;
                    border-top: 1px solid #e5e5e5;
                }
                .plan-card__detail-text {
                    margin: 0;
                    font-size: 13px;
                    line-height: 20px;
                    font-weight: 400;
                    color: #6d7175;
                    pointer-events: none;
                }
                .plan-card__detail-text s-button,
                .plan-card__detail-tooltip-content s-button {
                    pointer-events: auto;
                }
                .plan-card__detail-tooltip-content {
                    position: relative;
                    display: inline-block;
                    pointer-events: none;
                }
                .plan-card__muted {
                    color: #6d7175;
                }
                .plan-cards-slider__controls {
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                    margin-top: 16px;
                    width: calc(100% - 64px);
                    position: absolute;
                    left: 50%;
                    top: calc(50% - 24px);
                    transform: translate(-50%, -50%);
                    pointer-events: none;
                }
                .plan-cards-slider__arrow-group {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                    pointer-events: none;
                }
                .plan-cards-slider__arrow-count {
                    position: absolute;
                    top: -44px;
                    background-color: #fff;
                    color: #333;
                    font-weight: 500;
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 14px;
                    line-height: 20px;
                    white-space: nowrap;
                    box-shadow: 0 4px 8px #00000026;
                    pointer-events: none;
                }
                .plan-cards-slider__arrow {
                    pointer-events: auto;
                }
                .plan-cards-slider__arrow-count::after {
                    content: '';
                    position: absolute;
                    left: 50%;
                    top: 100%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 8px solid transparent;
                    border-right: 8px solid transparent;
                    border-top: 8px solid #fff;
                    filter: drop-shadow(0 1px 2px #0001);
                }
                .plan-cards-slider__arrow-count::before {
                    content: '';
                    position: absolute;
                    left: 50%;
                    top: 100%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 9px solid transparent;
                    border-right: 9px solid transparent;
                    border-top: 9px solid #E3E3E3;
                    z-index: -1;
                }
                .plan-cards-slider__dots {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    margin-top: 16px;
                }
                .plan-cards-slider__dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #ccc;
                    cursor: pointer;
                }
                .plan-cards-slider__dot--active {
                    background: #0094d5;
                    width: 12px;
                    height: 12px;
                }
                .is-visible { visibility: visible; }
                .is-hidden  { visibility: hidden; }

                /* ── Responsive ── */
                @media screen and (max-width: 768px) {
                    .app-manager__width-container.plan-cards-slider {
                        max-width: calc(100% + 8px);
                        padding-left: 0;
                        padding-right: 0;
                        margin-left: -4px;
                        margin-right: -4px;
                    }
                    .plan-cards-slider__controls {
                        display: none !important;
                    }
                }
                @media screen and (max-width: 640px) {
                    .plan-cards-slider__slide {
                        flex: 0 0 100%;
                    }
                }
            </style>

            <div class="plan-cards-slider app-manager__width-container">
                <div class="plan-cards-slider__embla" aria-label="Plan carousel" tabindex="0">
                    <div class="plan-cards-slider__container"></div>
                </div>
                <div class="plan-cards-slider__controls">
                    <span class="plan-cards-slider__arrow-group">
                        <span id="left-tooltip" class="plan-cards-slider__arrow-count"></span>
                        <s-button class="plan-cards-slider__arrow plan-cards-slider__arrow--prev" variant="primary" icon="arrow-left" aria-label="Previous plan" accessibilityLabel="Previous plan"></s-button>
                    </span>
                    <span class="plan-cards-slider__arrow-group">
                        <span id="right-tooltip" class="plan-cards-slider__arrow-count"></span>
                        <s-button class="plan-cards-slider__arrow plan-cards-slider__arrow--next" variant="primary" icon="arrow-right" aria-label="Next plan" accessibilityLabel="Next plan"></s-button>
                    </span>
                </div>
                <div class="plan-cards-slider__dots" aria-label="Carousel navigation"></div>
            </div>
        `;
        this._container = this.shadowRoot.querySelector('.plan-cards-slider__container');
    }

    _render() {
        try {
            const interval = this._selectedInterval === ANNUAL ? ANNUAL : EVERY_30_DAYS;
            const plans = this._getPlansByInterval(interval);
            const container = this.shadowRoot.querySelector('.plan-cards-slider__container');
            if (!container) {
                return;
            }
            this._removeDotListeners();
            this._dotListeners = [];
            container.innerHTML = '';

            const anyPlanHasDetails = plans.some(plan => getSortedPlanDetails(plan).length > 0);

            plans.forEach((plan) => {
                const features = preparePlanFeatures(plan, this._features, 4, this._showOnlyHighlights);
                const cardHTML = this._renderPlanCard(plan, features, anyPlanHasDetails);
                const slide = document.createElement('div');
                slide.className = 'plan-cards-slider__slide';
                slide.innerHTML = cardHTML;

                // Handle app-button
                const button = slide.querySelector('.plan-card__button');
                if (button) {
                    let clickHandledInTick = false;
                    const onPlanButtonActivate = () => {
                        if (clickHandledInTick) return;
                        clickHandledInTick = true;
                        queueMicrotask(() => {
                            clickHandledInTick = false;
                        });

                        this._captureCurrentSnapIndex();
                        this._selectedPlanId = plan.id;
                        this._render();

                        if (this._hasPlanChangeConfirmationContent(plan)) {
                            const content = getPlanChangeConfirmationContent(plan);
                            emitEvent(this, 'app-manager:plan-change-confirmation-request', {
                                source: 'plan-card-slider-polaris',
                                plan,
                                title: content?.title || null,
                                description: content?.description || null,
                                descriptionLines: Array.isArray(content?.descriptionLines) ? content.descriptionLines : [],
                            });
                            return;
                        }
                        this._executePlanChange(plan);
                    };

                    // `s-button` uses native `click`.
                    button.addEventListener('click', onPlanButtonActivate);
                }

                container.appendChild(slide);
            });
            this._setDynamicSlideWidths();
            if (plans.length > 0) {
                this._initCarousel(this._pendingSnapIndex);
            } else if (this._embla) {
                this._embla.destroy();
                this._embla = null;
                this._pendingSnapIndex = null;
                const dotsContainer = this.shadowRoot.querySelector('.plan-cards-slider__dots');
                if (dotsContainer) {
                    dotsContainer.innerHTML = '';
                    dotsContainer.style.display = 'none';
                }
            }
        } catch (e) {
            console.error('Error in PlanCardSliderPolaris._render:', e);
        }
    }

    _renderPlanCard(plan, features, anyPlanHasDetails = false) {
        try {
            const planId = plan.id;
            const name = plan.name || '';
            let price = Number(plan.price);
            let strikePrice = Number(plan.strike_price);
            if (isNaN(price)) {
                console.error('Invalid plan.price for plan', plan);
                price = 0;
            }
            if (plan.has_discount && isNaN(strikePrice)) {
                console.error('Invalid plan.strike_price for plan', plan);
                strikePrice = null;
            }

            const trialDays = Number(plan.trial_days) || 0;
            const trialDaysText = plan.trial_days_text
                ? translateMe(plan.trial_days_text)
                : (trialDays > 0 ? `${trialDays}${translateMe('-day free trial')}` : '');

            const interval = plan.interval || EVERY_30_DAYS;
            const isAnnualPlan = interval === ANNUAL;
            const displayPrice = isAnnualPlan ? (price / 12) : price;
            const displayStrikePrice = strikePrice ? (isAnnualPlan ? (strikePrice / 12) : strikePrice) : null;
            const displayIntervalLabel = isAnnualPlan ? getIntervalShortLabel(EVERY_30_DAYS) : getIntervalShortLabel(interval);
            const hasActiveCharge = this._hasActiveCharge;
            const buttonDisabled = isPlanButtonDisabled(this._shopifyPlan, plan, hasActiveCharge, this._currentPlan) || !!this._loadingPlanId;
            const anyPlanHasDiscount = this._getDiscountFlag();
            const isThisPlanSelected = this._selectedPlanId === planId;
            const isThisPlanLoading = this._loadingPlanId === planId;
            const isHighlighted = isThisPlanSelected || (!this._selectedPlanId && plan.most_popular);
            const buttonContent = getPlanButtonText(this._shopifyPlan, plan, this._currentPlan, false, hasActiveCharge);

            const badgeText = plan.plan_badge
                ? translateMe(plan.plan_badge)
                : (plan.most_popular ? translateMe('Most popular') : '');

            // A discount badge replaces the plan badge; an active discount on the
            // current plan also gets a hover tooltip (end date + full price).
            const discountBadge = plan.discount_badge && plan.discount_badge.text ? plan.discount_badge : null;
            const badgeTooltip = discountBadge && Array.isArray(discountBadge.tooltip) && discountBadge.tooltip.length ? discountBadge.tooltip : null;
            const headerBadgeHtml = discountBadge
                ? `<span class="plan-card__badge-group">
                            <span class="plan-card__badge plan-card__badge-discount${discountBadge.active ? ' plan-card__badge-discount--active' : ''}">
                                <span class="plan-card__badge-text">${discountBadge.text}</span>
                            </span>
                            ${badgeTooltip ? `<span class="plan-card__badge-info" tabindex="0" role="button" aria-label="${translateMe('Discount details')}">
                                <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM7.25 6.75a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-1.5 0v-4ZM8 3.6a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"/></svg>
                                <span class="plan-card__badge-tooltip" role="tooltip">${badgeTooltip.map(line => `<span>${line}</span>`).join('')}</span>
                            </span>` : ''}
                        </span>`
                : (badgeText ? `<span class="plan-card__badge">${badgeText}</span>` : '');

            return `
                <div class="plan-card ${isHighlighted ? 'plan-card--highlighted' : ''} ${isThisPlanLoading ? 'plan-card--selecting' : ''}">
                    <div class="plan-card__header">
                        <h3 class="plan-card__heading">${name}</h3>
                        ${headerBadgeHtml}
                    </div>

                    <!-- Price -->
                    <div class="plan-card__price-section ${anyPlanHasDiscount ? 'plan-card__price-section--any-plan-has-discount' : ''}">
                        <div class="plan-card__strike-price-row">
                            ${displayStrikePrice ? `
                                <span class="plan-card__strike-price">$${displayStrikePrice.toFixed(2)}</span>
                                <span class="plan-card__plan-interval plan-card__muted">/${displayIntervalLabel}</span>
                            ` : ''}
                        </div>
                        <div class="plan-card__price-row">
                            ${displayPrice > 0 ? `
                                <span class="plan-card__price">$${displayPrice.toFixed(2)}</span>
                                <span class="plan-card__plan-interval plan-card__muted">/${displayIntervalLabel}</span>
                            ` : `
                                <span class="plan-card__price">${translateMe('FREE')}</span>
                            `}
                        </div>
                    </div>

                    <!-- Description -->
                    ${isAnnualPlan ? `
                        <p class="plan-card__description-text">${translateMe('Billed at')} <span>$${price.toFixed(2)}</span> ${translateMe('once per year')}</p>
                    ` : (this._selectedInterval === ANNUAL && isFreePlan(plan) ? `
                        <p class="plan-card__description-text" style="visibility: hidden;" aria-hidden="true">${translateMe('Billed at')} <span>$${price.toFixed(2)}</span> ${translateMe('once per year')}</p>
                    ` : '')}

                    <!-- CTA Button -->
                    <div class="plan-card__button-wrapper">
                        <s-button
                            class="plan-card__button"
                            variant="primary"
                            inlineSize="fill"
                            aria-label="Choose Plan"
                            tabindex="0"
                            ${buttonDisabled ? 'disabled' : ''}
                            ${isThisPlanLoading ? 'loading' : ''}
                        >${buttonContent}</s-button>
                    </div>

                    <!-- Trial Badge -->
                    ${trialDaysText
                        ? `<span class="plan-card__badge plan-card__badge-trial">${trialDaysText}</span>`
                        : `<div class="plan-card__trial-placeholder"></div>`
                    }

                    <!-- Features -->
                    <ul class="plan-card__feature-list">
                        ${features.map(feature => `
                            <li class="plan-card__feature">
                                <span class="plan-card__feature-icon" aria-hidden="true">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M15.7803 5.96967C16.0732 6.26256 16.0732 6.73744 15.7803 7.03033L9.28033 13.5303C8.98744 13.8232 8.51256 13.8232 8.21967 13.5303L4.96967 10.2803C4.67678 9.98744 4.67678 9.51256 4.96967 9.21967C5.26256 8.92678 5.73744 8.92678 6.03033 9.21967L8.75 11.9393L14.7197 5.96967C15.0126 5.67678 15.4874 5.67678 15.7803 5.96967Z" fill="#1A1A1A"/>
                                    </svg>
                                </span>
                                <span class="plan-card__muted">
                                    ${feature.value_type !== 'boolean' ? translateMe(formatFeature(feature, null)) : ''}
                                    ${translateMe(feature.name)}
                                </span>
                            </li>
                        `).join('')}
                    </ul>

                    <!-- Detail Tooltip -->
                    ${anyPlanHasDetails
                        ? `
                            <div class="plan-card__detail-tooltip-wrapper" style="visibility:${anyPlanHasDetails ? 'visible' : 'hidden'}">
                                <s-stack>
                                    ${(() => {
                                        const primaryDetail = getPrimaryPlanDetail(plan);
                                        const additionalDetails = getAdditionalPlanDetails(plan);
                                        if (!primaryDetail) return '';
                                        const detailId = `detail-tooltip-${String(planId).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                                        return `
                                            <div class="plan-card__detail-tooltip">
                                                <div class="plan-card__detail-tooltip-content">
                                                    <s-paragraph color="subdued">
                                                        ${translateMe(primaryDetail.name)}
                                                        ${additionalDetails.length > 0 ? `
                                                            <s-button
                                                                icon="info"
                                                                interestFor="${detailId}"
                                                                variant="tertiary"
                                                                accessibilityLabel="View additional billing details"
                                                            ></s-button>
                                                        ` : ''}
                                                    </s-paragraph>
                                                    ${additionalDetails.length > 0 ? `
                                                        <s-tooltip id="${detailId}">
                                                            ${additionalDetails.map(d => translateMe(d.name)).join(' ')}
                                                        </s-tooltip>
                                                    ` : ''}
                                                </div>
                                            </div>
                                        `;
                                    })()}
                                </s-stack>
                            </div>`
                        : ''
                    }

                </div>
            `;
        } catch (e) {
            console.error('Error in _renderPlanCard:', e, plan);
            return '<div class="plan-card plan-card--error">Error rendering plan card</div>';
        }
    }


    // --- DOM Update Helpers ---
    _setDynamicSlideWidths() {
        const slides = this.shadowRoot.querySelectorAll('.plan-cards-slider__slide');
        const plansCount = this._plans.length;
        const slidesPerView = this._getSlidesPerView();
        if (plansCount === 0) return;

        const effectiveSlides = Math.min(slidesPerView, plansCount);
        slides.forEach(slide => {
            slide.style.flex = `0 0 ${100 / effectiveSlides}%`;
        });

        const container = this.shadowRoot.querySelector('.plan-cards-slider');
        const maxWidths = { 2: 808, 3: 1208, 4: 1448, 5: 1608 };
        if (maxWidths[effectiveSlides]) {
            container.style.maxWidth = `${maxWidths[effectiveSlides]}px`;
        } else {
            container.style.maxWidth = '';
        }
    }

    _updateArrowVisibilityAndTooltips() {
        if (!this._embla) return;
        const prevBtn = this.shadowRoot.querySelector('.plan-cards-slider__arrow--prev');
        const nextBtn = this.shadowRoot.querySelector('.plan-cards-slider__arrow--next');
        const prevTooltip = this.shadowRoot.querySelector('#left-tooltip');
        const nextTooltip = this.shadowRoot.querySelector('#right-tooltip');
        const slidesPerView = this._getSlidesPerView();
        const total = this._plans.length;
        const selected = this._embla.selectedScrollSnap();
        const canScrollPrev = this._embla.canScrollPrev();
        const canScrollNext = this._embla.canScrollNext();
        const leftCount = selected;
        const rightCount = total - (selected + slidesPerView);
        prevBtn.style.display = canScrollPrev ? '' : 'none';
        nextBtn.style.display = canScrollNext ? '' : 'none';
        if (prevTooltip) {
            prevTooltip.textContent = leftCount > 0 ? `+${leftCount} ${translateMe(leftCount === 1 ? 'plan' : 'plans')}` : '';
            prevTooltip.style.display = canScrollPrev && leftCount > 0 ? '' : 'none';
        }
        if (nextTooltip) {
            nextTooltip.textContent = rightCount > 0 ? `+${rightCount} ${translateMe(rightCount === 1 ? 'plan' : 'plans')}` : '';
            nextTooltip.style.display = canScrollNext && rightCount > 0 ? '' : 'none';
        }
    }

    _updateDotsVisibility() {
        const dotsContainer = this.shadowRoot.querySelector('.plan-cards-slider__dots');
        if (!dotsContainer || !this._embla) return;
        dotsContainer.style.display = this._embla.scrollSnapList().length > 1 ? '' : 'none';
    }

    _removeDotListeners() {
        this._dotListeners.forEach(({dot, handler}) => {
            dot.removeEventListener('click', handler);
        });
        this._dotListeners = [];
    }

    _captureCurrentSnapIndex() {
        if (!this._embla) {
            this._pendingSnapIndex = null;
            return;
        }
        this._pendingSnapIndex = this._embla.selectedScrollSnap();
    }


    // --- External Library/Integration ---
    _initCarousel(restoreSnapIndex = null) {
        const viewport = this.shadowRoot.querySelector('.plan-cards-slider__embla');
        if (!viewport || !this._plans.length) return;
        if (this._embla) this._embla.destroy();
        this._embla = EmblaCarousel(viewport, {
            align: 'start',
            loop: false,
            dragFree: false,
        });
        this._initControls();
        this._createDots();

        if (Number.isInteger(restoreSnapIndex)) {
            const maxSnapIndex = this._embla.scrollSnapList().length - 1;
            const targetSnapIndex = Math.max(0, Math.min(restoreSnapIndex, maxSnapIndex));
            this._embla.scrollTo(targetSnapIndex, true);
        }

        this._pendingSnapIndex = null;
        this._embla.on('select', () => this._updateArrowVisibilityAndTooltips());
        this._embla.on('reInit', () => {
            this._updateArrowVisibilityAndTooltips();
            this._createDots();
        });
        this._updateArrowVisibilityAndTooltips();
    }

    _initControls() {
        const prev = this.shadowRoot.querySelector('.plan-cards-slider__arrow--prev');
        const next = this.shadowRoot.querySelector('.plan-cards-slider__arrow--next');
        if (!prev || !next) return;
        if (this._onPrevClick && prev.removeEventListener) prev.removeEventListener('click', this._onPrevClick);
        if (this._onNextClick && next.removeEventListener) next.removeEventListener('click', this._onNextClick);
        this._onPrevClick = () => this._embla && this._embla.scrollPrev();
        this._onNextClick = () => this._embla && this._embla.scrollNext();
        prev.addEventListener('click', this._onPrevClick);
        next.addEventListener('click', this._onNextClick);
        if (this._embla && this._embla.on) {
            if (this._onEmblaReInit) this._embla.off && this._embla.off('reInit', this._onEmblaReInit);
            this._onEmblaReInit = () => {
                this._createDots();
            };
            this._embla.on('reInit', this._onEmblaReInit);
        }
    }

    // --- Confirmation Modal ---
    _hasPlanChangeConfirmationContent(plan) {
        const content = getPlanChangeConfirmationContent(plan);
        if (!(content?.title || content?.description)) return false;
        return isPlanDowngrade(plan, this._currentPlan, this._hasActiveCharge);
    }

    _executePlanChange(plan) {
        if (!plan || !plan.id || this._loadingPlanId) return;
        this._captureCurrentSnapIndex();
        this._loadingPlanId = plan.id;
        this._render();
        emitEvent(this, 'app-manager:plan-click', { plan });
        setTimeout(() => {
            this._captureCurrentSnapIndex();
            this._loadingPlanId = null;
            this._render();
        }, 2000);
    }

    _createDots() {
        const dotsContainer = this.shadowRoot.querySelector('.plan-cards-slider__dots');
        if (!dotsContainer || !this._embla) return;
        dotsContainer.innerHTML = '';
        const scrollSnaps = this._embla.scrollSnapList();

        if (this._onDotsSelect && this._embla.off) {
            this._embla.off('select', this._onDotsSelect);
        }

        this._onDotsSelect = null;

        if (scrollSnaps.length <= 1) {
            this._updateDotsVisibility();
            return;
        }

        scrollSnaps.forEach((_, index) => {
            const dot = document.createElement('div');
            dot.className = 'plan-cards-slider__dot';
            const handler = () => { this._embla && this._embla.scrollTo(index); };
            dot.addEventListener('click', handler);
            this._dotListeners.push({dot, handler});
            dotsContainer.appendChild(dot);
        });
        this._onDotsSelect = () => {
            if (!this._embla) return;
            const selected = this._embla.selectedScrollSnap();
            dotsContainer.querySelectorAll('.plan-cards-slider__dot').forEach((dot, i) => {
                dot.classList.toggle('plan-cards-slider__dot--active', i === selected);
            });
        };

        this._embla.on && this._embla.on('select', this._onDotsSelect);
        this._updateDotsVisibility();
        this._onDotsSelect();
    }
}

if (!customElements.get('plan-card-slider-polaris')) {
    customElements.define('plan-card-slider-polaris', PlanCardSliderPolaris);
}

export { PlanCardSliderPolaris };